import { Navigate } from 'react-router-dom';
import { useGetMyMembershipQuery, useGetPracticeQuery } from '@api/endpoints/onboardingApi';

export interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Guards the onboarding routes (`/onboarding-path`, `/setup-practice`,
 * `/join-practice`) so a user who already belongs to — or has created — a
 * practice cannot re-enter registration.
 *
 * This is the frontend half of the single-practice-per-user rule (Option 1);
 * the backend (`POST /practice`, `POST /practice/:id/join`) returns 409 for the
 * same condition. Users already attached to a practice are funneled to `/home`,
 * where `RoleGuard` routes pending/rejected states to the correct status page.
 *
 * A brand-new user (no membership, no practice) falls through to the children
 * so first-time onboarding still works.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { data: membershipData, isLoading: isMembershipLoading } = useGetMyMembershipQuery();
  const { data: practiceData, isLoading: isPracticeLoading } = useGetPracticeQuery();

  // Avoid a redirect flash before the membership/practice data resolves.
  if (isMembershipLoading || isPracticeLoading) {
    return null;
  }

  const membership = membershipData?.membership;
  const hasActiveOrPendingMembership =
    !!membership && (membership.status === 'active' || membership.status === 'pending');
  const hasPractice = Array.isArray(practiceData) && practiceData.length > 0;

  if (hasActiveOrPendingMembership || hasPractice) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}
